import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

const EXPOSE_DRAFT_IMPORT_QUERY_KEY = 'importExposeDraft';
const EXPOSE_DRAFT_IMPORT_WINDOW_QUERY_KEY = 'importExposeDraftWindow';
const EXPOSE_DRAFT_WINDOW_NAME_PREFIX = 'enpowerExposeDraft:';
const EXPOSE_SAVED_DRAFTS_STORAGE_KEY = 'enpower.exposes.savedDrafts.v1';
const EXPOSE_BULK_DRAFT_STORAGE_KEY = 'enpower.exposes.rawBulkDraft.v1';
const EXPOSE_IMPORT_NOTICE_KEY = 'enpower.exposes.importNotice.v1';

function decodeBase64UrlJson(value) {
  const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = window.atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function normalizeExposeDraftEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const projects = Array.isArray(entry.projects)
    ? entry.projects.filter((project) => project && typeof project === 'object')
    : [];
  if (!projects.length && !String(entry.bulkText || '').trim()) return null;
  const projectIds = new Set(projects.map((project) => String(project.id || '')).filter(Boolean));
  return {
    id: String(entry.id || `saved-${Date.now()}`),
    name: String(entry.name || 'Exposé-Vorgang').trim() || 'Exposé-Vorgang',
    savedAt: String(entry.savedAt || new Date().toISOString()),
    mediaLimited: entry.mediaLimited === true,
    projects,
    selectedIds: Array.isArray(entry.selectedIds)
      ? entry.selectedIds.map(String).filter((id) => projectIds.has(id))
      : [],
    activeId: projectIds.has(String(entry.activeId || '')) ? String(entry.activeId || '') : '',
    bulkField: String(entry.bulkField || 'status'),
    bulkText: String(entry.bulkText || ''),
    activeVariantKey: String(entry.activeVariantKey || 'expose-projektverkauf'),
  };
}

function importExposeDraftFromUrl() {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const shouldReadWindowName = params.get(EXPOSE_DRAFT_IMPORT_WINDOW_QUERY_KEY) === '1';
  const windowNameDraft = shouldReadWindowName && String(window.name || '').startsWith(EXPOSE_DRAFT_WINDOW_NAME_PREFIX)
    ? String(window.name || '').slice(EXPOSE_DRAFT_WINDOW_NAME_PREFIX.length)
    : '';
  const encodedDraft = params.get(EXPOSE_DRAFT_IMPORT_QUERY_KEY) || windowNameDraft;
  if (!encodedDraft) return;

  try {
    const draft = normalizeExposeDraftEntry(decodeBase64UrlJson(encodedDraft));
    if (!draft) throw new Error('Der Import enthält keinen gültigen Exposé-Vorgang.');

    const rawSaved = window.localStorage.getItem(EXPOSE_SAVED_DRAFTS_STORAGE_KEY);
    let parsedSaved = { version: 1, drafts: [] };
    try {
      parsedSaved = rawSaved ? JSON.parse(rawSaved) : parsedSaved;
    } catch (_) {
      parsedSaved = { version: 1, drafts: [] };
    }

    const currentDrafts = Array.isArray(parsedSaved?.drafts) ? parsedSaved.drafts : [];
    const nextDrafts = [
      draft,
      ...currentDrafts.filter((item) => item && item.id !== draft.id),
    ].slice(0, 25);

    window.localStorage.setItem(EXPOSE_SAVED_DRAFTS_STORAGE_KEY, JSON.stringify({
      version: 1,
      drafts: nextDrafts,
    }));
    window.localStorage.setItem(EXPOSE_BULK_DRAFT_STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: draft.savedAt,
      mediaLimited: draft.mediaLimited,
      projects: draft.projects,
      selectedIds: draft.selectedIds,
      activeId: draft.activeId,
      bulkField: draft.bulkField,
      bulkText: draft.bulkText,
      activeVariantKey: draft.activeVariantKey,
    }));
    window.sessionStorage.setItem(EXPOSE_IMPORT_NOTICE_KEY, JSON.stringify({
      type: 'success',
      name: draft.name,
    }));
  } catch (error) {
    window.sessionStorage.setItem(EXPOSE_IMPORT_NOTICE_KEY, JSON.stringify({
      type: 'error',
      message: error?.message || 'Exposé-Vorgang konnte nicht importiert werden.',
    }));
  } finally {
    if (windowNameDraft) window.name = '';
    params.delete(EXPOSE_DRAFT_IMPORT_QUERY_KEY);
    params.delete(EXPOSE_DRAFT_IMPORT_WINDOW_QUERY_KEY);
    if (!params.get('view')) params.set('view', 'exposes-automation');
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      document.title,
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`,
    );
  }
}

importExposeDraftFromUrl();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
